import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BsClock, BsPersonPlusFill, BsTags, BsXCircle } from 'react-icons/bs';
import { FaRegTrashAlt } from 'react-icons/fa';
import { GoPlus } from 'react-icons/go';
import { useNavigate, useSearchParams } from 'react-router';

import {
  AppointmentInfo,
  Button,
  LoadingOverlay,
  ResponsiveModalBottomSheet,
  StickyFooter,
} from '@/components';
import { useHighlightNewItem } from '@/hooks';
import { BookingService } from '@/services';
import { useBookingStore, useBrandStore } from '@/store';

import CoupleTag from './components/tags/CoupleTag';
import TermsAndConditionsModal from './components/TermsAndConditionsModal';
import TreatmentListModalPage from './components/TreatmentListModalPage';

const SelectTreatment: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [pendingDeleteCustomerIndex, setPendingDeleteCustomerIndex] = useState<
    number | null
  >(null);
  const {
    availabilityId,
    customers,
    addCustomer,
    availableTreatments,
    isStep2Complete,
    storeId,
  } = useBookingStore();
  const [, setSearchParams] = useSearchParams();
  const {
    getMaxCustomerCount,
    setStoreId: setBrandStoreId,
    getShowTermsAndConditions,
  } = useBrandStore();

  const maxCustomerCount = getMaxCustomerCount();
  const isMaxCustomers = customers.length >= maxCustomerCount;

  // Hook for highlighting newly added customers
  const {
    itemRefs: itemRefsRef,
    highlightedKey,
    highlightItem,
  } = useHighlightNewItem<HTMLDivElement>();

  // helpers to build keys for customers and treatments
  const customerKey = (index: number) => `customer-${index}`;
  const treatmentKey = (customerIndex: number, treatmentIndex: number) =>
    `customer-${customerIndex}-treatment-${treatmentIndex}`;

  // convenience bindings
  const customerRefs = itemRefsRef; // for readability in JSX

  // Treatment list modal state
  const [showTreatments, setShowTreatments] = useState(false);
  const [currentCustomerIndex, setCurrentCustomerIndex] = useState<
    number | null
  >(null);

  useEffect(() => {
    if (storeId > 0) {
      setBrandStoreId(storeId);
      BookingService.validateBooking();
    }
  }, [storeId, setBrandStoreId]);

  const openTreatmentList = (customerIndex: number) => {
    setCurrentCustomerIndex(customerIndex);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('customerIndex', String(customerIndex));
      return p;
    });
    setShowTreatments(true);
  };

  const closeTreatmentList = () => {
    setShowTreatments(false);
    setCurrentCustomerIndex(null);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete('customerIndex');
      return p;
    });
  };

  const handleAddTreatment = (customerIndex: number) =>
    openTreatmentList(customerIndex);

  const handleRemoveCustomer = async (customerIndex: number) => {
    // Prevent removing the last customer
    if (customers.length <= 1) return;

    const customer = customers[customerIndex];
    // If the customer has no treatments yet, just remove from local store
    if (!customer?.treatmentItems || customer.treatmentItems.length === 0) {
      const { removeCustomer } = useBookingStore.getState();
      removeCustomer(customerIndex);
      return;
    }

    // Check if customer has any treatments with partnerCustomerIndex
    const hasPartnerTreatments = customer.treatmentItems.some(
      (item) => item.partnerCustomerIndex != null,
    );

    if (hasPartnerTreatments) {
      setPendingDeleteCustomerIndex(customerIndex);
      setShowDeleteConfirmation(true);
    } else {
      await performCustomerDeletion(customerIndex);
    }
  };

  const performCustomerDeletion = async (customerIndex: number) => {
    setIsDeleting(true);
    try {
      await BookingService.deleteTreatment(customerIndex, -1);
    } catch (error) {
      console.error('Failed to remove customer:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (pendingDeleteCustomerIndex !== null) {
      setShowDeleteConfirmation(false);
      await performCustomerDeletion(pendingDeleteCustomerIndex);
      setPendingDeleteCustomerIndex(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setPendingDeleteCustomerIndex(null);
  };

  const handleRemoveTreatment = async (
    customerIndex: number,
    treatmentIndex: number,
  ) => {
    setIsDeleting(true);
    try {
      await BookingService.deleteTreatment(customerIndex, treatmentIndex);
    } catch (error) {
      console.error('Failed to remove treatment:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddCustomer = () => {
    if (isMaxCustomers) {
      return;
    }

    const newIndex = customers.length;
    addCustomer();
    highlightItem(customerKey(newIndex));
  };

  const handleNext = () => {
    if (isStep2Complete()) {
      if (getShowTermsAndConditions()) {
        setShowTermsModal(true);
      } else {
        handleTermsAccept();
      }
    }
  };

  const handleTermsAccept = () => {
    setShowTermsModal(false);
    navigate('/booking/summary');
  };

  const handleTermsClose = () => {
    setShowTermsModal(false);
  };

  // Local TreatmentCostSummary component - uses booking store for calculations
  const TreatmentCostSummary: React.FC = () => {
    const {
      balance,
      getTotalTreatments,
      getMaxDuration,
      getStartTime,
      getEndTime,
    } = useBookingStore();
    const total = balance.total;
    const currencyLabel = t('common.currency');

    // Check if any customer has no treatments via store action (use selector)
    const anyCustomerMissingTreatment = useBookingStore((s) =>
      s.hasMissingTreatments(),
    );

    // Local UI flag: only show the validation error after user attempted to continue once
    const [showTreatmentError, setShowTreatmentError] = useState(false);

    // Clear the error automatically when all customers have treatments
    useEffect(() => {
      if (!anyCustomerMissingTreatment) {
        setShowTreatmentError(false);
      }
    }, [anyCustomerMissingTreatment]);

    // calculate booking summary using store methods
    const computeBookingSummary = () => {
      if (!availabilityId || customers.length === 0) return '';

      const totalTreatments = getTotalTreatments();
      if (totalTreatments === 0) return '';

      const maxDuration = getMaxDuration();
      const startTime = getStartTime();
      const endTime = getEndTime();

      if (!startTime || !endTime) return '';

      const formatTime = (date: dayjs.Dayjs) => date.format('HH:mm');
      const treatmentText =
        totalTreatments === 1
          ? t('treatment.label')
          : t('treatment.labelPlural');
      const hours = Math.floor(maxDuration / 60);
      const minutes = maxDuration % 60;
      const durationText =
        maxDuration >= 60
          ? `${hours} ${t('treatment.hoursShort')}${minutes > 0 ? ` ${minutes} ${t('common.min')}` : ''}`
          : `${maxDuration} ${t('common.min')}`;
      return `${totalTreatments} ${treatmentText} ${durationText}. (${formatTime(startTime)}-${formatTime(endTime)})`;
    };

    const summaryText = computeBookingSummary();

    return (
      <div className="space-y-2">
        <div className="flex w-full items-start justify-between">
          <span className="text-xl leading-7 font-semibold text-black">
            {t('payment.total')}
          </span>
          <span className="text-xl leading-7 font-semibold text-black">
            {currencyLabel}
            {total.toLocaleString()}
          </span>
        </div>

        {summaryText && (
          <div className="text-secondary-text leading-6">{summaryText}</div>
        )}

        {/* Show validation message only after user clicked CTA once and still missing treatment */}
        {showTreatmentError && anyCustomerMissingTreatment && (
          <div className="text-error mb-2 leading-5 font-normal">
            {t('validation.treatmentRequired')}
          </div>
        )}

        <Button
          onClick={() => {
            if (isStep2Complete()) {
              setShowTreatmentError(false);
              handleNext();
            } else {
              setShowTreatmentError(true);
            }
          }}
        >
          {t('treatment.continueToPayment')}
        </Button>
      </div>
    );
  };

  return (
    <div className="relative">
      <div className="flex flex-col space-y-6 pb-40 md:flex-row md:gap-6 md:pb-0">
        {/* Customer Treatments */}
        <div className="md:shadow-card space-y-4 md:w-2/3 md:rounded-2xl md:p-6">
          {customers.map((customer, index) => (
            <div
              key={index}
              ref={(el) => {
                customerRefs.current[customerKey(index)] = el;
              }}
              className={`relative rounded-lg border border-slate-200 bg-white p-4 ${
                highlightedKey === customerKey(index)
                  ? 'animate-border-blink'
                  : ''
              }`}
            >
              {/* Customer Header */}
              <div className="mb-4 flex h-6 w-full items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-lg leading-7 font-semibold text-black">
                    {t('payment.customer')} {index + 1}
                  </h4>
                </div>
                {/* Remove Customer Button - Only show if more than 1 customer */}
                {customers.length > 1 && (
                  <button
                    onClick={() => handleRemoveCustomer(index)}
                    className="flex items-center justify-center rounded transition-opacity hover:opacity-90 focus:ring-2 focus:ring-red-100 focus:outline-none md:cursor-pointer"
                    aria-label={t('treatment.removeCustomer')}
                  >
                    <BsXCircle className="text-error h-6 w-6 transition-transform hover:scale-105" />
                  </button>
                )}
              </div>

              {/* Treatments Section */}
              {customer.treatmentItems && customer.treatmentItems.length > 0 ? (
                <div className="mb-4 space-y-2">
                  {/* Treatment Label */}
                  <div className="text-secondary-text leading-6 font-semibold">
                    {t('treatment.label')}
                  </div>

                  {/* Treatment Cards */}
                  {customer.treatmentItems.map(
                    (customerTreatment, treatmentIndex) => {
                      const treatment = availableTreatments.find(
                        (t) => t.treatmentId === customerTreatment.treatmentId,
                      );
                      const option = treatment?.options.find(
                        (o) => o.optionId === customerTreatment.optionId,
                      );
                      const durationMinutes =
                        option?.duration ?? customerTreatment.optionId;
                      const price =
                        customerTreatment.retailPrice > 0
                          ? customerTreatment.retailPrice
                          : customerTreatment.originalPrice;

                      return (
                        <div
                          key={treatmentIndex}
                          ref={(el) => {
                            customerRefs.current[
                              treatmentKey(index, treatmentIndex)
                            ] = el;
                          }}
                          className={`border-border-default relative rounded-lg border bg-white p-3 ${
                            highlightedKey ===
                            treatmentKey(index, treatmentIndex)
                              ? 'animate-border-blink'
                              : ''
                          }`}
                        >
                          {/* Treatment Content */}
                          <div className="mb-1 flex items-center justify-between">
                            <div className="flex-1">
                              <div className="text-primary-text leading-6">
                                {treatment?.name ||
                                  `Treatment ${customerTreatment.treatmentId}`}
                              </div>
                            </div>
                            {/* Delete Treatment Button */}
                            <button
                              onClick={() =>
                                handleRemoveTreatment(index, treatmentIndex)
                              }
                              className="flex h-6 w-6 items-center justify-center rounded text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:ring-2 focus:ring-gray-200 focus:outline-none md:cursor-pointer"
                              aria-label={t('treatment.removeTreatment')}
                            >
                              <FaRegTrashAlt className="h-6 w-6 transition-transform hover:scale-105" />
                            </button>
                          </div>

                          {/* Treatment Details */}
                          <div className="flex items-center gap-3">
                            {/* Duration */}
                            <div className="flex items-center gap-1">
                              <BsClock className="text-secondary-text h-3.5 w-3.5" />
                              <span className="text-secondary-text text-sm leading-5">
                                {durationMinutes > 0
                                  ? durationMinutes
                                  : t('treatment.unknown')}{' '}
                                {t('common.min')}
                              </span>
                            </div>

                            {/* Price */}
                            <div className="flex items-center gap-1">
                              <BsTags className="text-secondary-text h-4 w-4" />
                              <span className="text-secondary-text text-sm leading-5">
                                {price} THB
                              </span>
                            </div>
                          </div>

                          {/* Partner info (shows when treatment is partnered) */}
                          {customerTreatment.partnerCustomerIndex != null && (
                            <div className="mt-1 flex items-center gap-2">
                              <span className="text-secondary-text font-semibold">
                                {t('treatment.partneredWith')}
                              </span>
                              <CoupleTag
                                text={`${t('payment.customer')} ${
                                  customerTreatment.partnerCustomerIndex + 1
                                }`}
                              />
                            </div>
                          )}
                        </div>
                      );
                    },
                  )}
                </div>
              ) : (
                <div className="mb-4">{/* Empty state */}</div>
              )}

              {/* Add Treatment Button: prominent when no treatment yet, subtle when already added */}
              {customer.treatmentItems && customer.treatmentItems.length > 0 ? (
                // Less prominent: use plain HTML button (not the Button component) to match Figma
                <button
                  type="button"
                  onClick={() => handleAddTreatment(index)}
                  aria-label={t('treatment.addTreatment')}
                  className="hover:bg-bg-hover mt-4 flex cursor-pointer items-center gap-2 leading-7 font-semibold"
                >
                  <GoPlus className="text-primary h-4 w-4" />
                  <span className="text-primary">
                    {t('treatment.addTreatment')}
                  </span>
                </button>
              ) : (
                <Button
                  variant="secondary"
                  onClick={() => handleAddTreatment(index)}
                  className="gap-1.5"
                >
                  <GoPlus className="text-primary h-4 w-4" />
                  <span>{t('treatment.addTreatment')}</span>
                </Button>
              )}
            </div>
          ))}

          {/* Add Customer Button */}
          <button
            onClick={handleAddCustomer}
            disabled={isMaxCustomers}
            className={`flex w-full items-center justify-center gap-1.5 rounded-lg py-0.5 transition-colors hover:bg-gray-50 focus:ring-2 focus:ring-gray-200 focus:outline-none ${isMaxCustomers ? 'text-text-disabled md:cursor-not-allowed' : 'text-primary md:cursor-pointer'}`}
          >
            <BsPersonPlusFill className="h-6 w-6" />
            <span className="leading-7 font-semibold">
              {t('treatment.addCustomer')}
            </span>
          </button>
        </div>

        {/* Right side bar in desktop */}
        <div className="md:shadow-card hidden h-fit space-y-4 md:block md:w-1/3 md:rounded-2xl md:p-6">
          <AppointmentInfo className="border-0 !p-0" />
          <hr className="border-t border-gray-200" />
          <TreatmentCostSummary />
        </div>
      </div>

      {/* Sticky Footer with Navigation */}
      <StickyFooter className="md:hidden">
        <TreatmentCostSummary />
      </StickyFooter>

      {/* Terms and Conditions Modal */}
      <TermsAndConditionsModal
        isOpen={showTermsModal}
        onClose={handleTermsClose}
        onAccept={handleTermsAccept}
      />

      <TreatmentListModalPage
        isOpen={showTreatments}
        onClose={closeTreatmentList}
        customerIndex={currentCustomerIndex}
        onTreatmentAdded={(customerIdx, treatmentIdx) => {
          // First scroll/highlight the customer container
          highlightItem(customerKey(customerIdx));
          // Then highlight the new treatment (use a small timeout so scroll completes)
          setTimeout(() => {
            highlightItem(treatmentKey(customerIdx, treatmentIdx));
          }, 250);
        }}
      />

      {/* Delete Couple Treatment Confirmation */}
      <ResponsiveModalBottomSheet
        isOpen={showDeleteConfirmation}
        onClose={handleCancelDelete}
        showCloseOnMobile={false}
        closable={true}
      >
        <div className="flex flex-col gap-4 px-4 pb-6">
          <div className="flex flex-col gap-4 text-center">
            <p className="text-xl leading-7 font-semibold text-black">
              {t('treatment.deleteCoupleTreatmentTitle')}
            </p>
            <p className="text-secondary-text leading-6">
              {t('treatment.deleteCoupleTreatmentMessage')}
            </p>
          </div>
          <div className="flex gap-4">
            <Button
              variant="secondary"
              onClick={handleCancelDelete}
              className="flex-1"
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmDelete}
              className="flex-1"
            >
              {t('common.delete')}
            </Button>
          </div>
        </div>
      </ResponsiveModalBottomSheet>

      <LoadingOverlay isVisible={isDeleting} />
    </div>
  );
};

export default SelectTreatment;
